import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BarcodeScanner = ({ onScan, loading }) => {
  const [isScanning, setIsScanning] = useState(false);
  const [error, setError] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, []);

  const startScanner = async () => {
    try {
      setError('');
      setIsScanning(true);

      const html5QrCode = new Html5Qrcode('barcode-scanner-region');
      html5QrCodeRef.current = html5QrCode;

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0
      };

      await html5QrCode?.start(
        { facingMode: 'environment' },
        config,
        (decodedText) => {
          if (decodedText && !loading) {
            onScan(decodedText);
            stopScanner();
          }
        },
        (errorMessage) => {
          // Ignore scanning errors (happens continuously while scanning)
        }
      );
    } catch (err) {
      console.error('Error starting scanner:', err);
      setError('Unable to access camera. Please check permissions.');
      setIsScanning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef?.current) {
        await html5QrCodeRef?.current?.stop();
        html5QrCodeRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping scanner:', err);
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Scanner Region */}
      <div className="relative bg-muted rounded-lg overflow-hidden" style={{ minHeight: '300px' }}>
        {!isScanning && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <Icon name="Scan" size={64} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                Click Start Scanner to begin scanning barcodes
              </p>
              <Button
                onClick={startScanner}
                disabled={loading}
                iconName="Camera"
              >
                Start Scanner
              </Button>
            </div>
          </div>
        )}
        
        <div id="barcode-scanner-region" className="w-full" />
      </div>

      {/* Scanner Controls */}
      {isScanning && (
        <div className="flex justify-center">
          <Button
            onClick={stopScanner}
            variant="destructive"
            iconName="X"
          >
            Stop Scanner
          </Button>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive rounded-lg">
          <p className="text-sm text-destructive flex items-center gap-2">
            <Icon name="AlertCircle" size={16} />
            {error}
          </p>
        </div>
      )}

      {/* Instructions */}
      <div className="p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <Icon name="Info" size={18} />
          Scanning Instructions
        </h3>
        <ul className="text-sm text-muted-foreground space-y-1 ml-6 list-disc">
          <li>Position the barcode within the scanning frame</li>
          <li>Ensure good lighting for best results</li>
          <li>Hold steady until the barcode is detected</li>
          <li>Scanner will automatically process valid barcodes</li>
        </ul>
      </div>
    </div>
  );
};

export default BarcodeScanner;